import React from 'react'
import {SERIES_COLORS} from '../../constants'
import {fmN, fmP} from '../../helpers/format'
import useContainerWidth from '../../hooks/useContainerWidth'
import {tipHandlers, useTooltip} from './Tooltip'

const MONO = 'SFMono-Regular,Consolas,monospace'

/**
 * Cột chồng + đường Total nối đỉnh cột, port từ barLine() của bản mẫu.
 * series: [{label, color?, values: [number|null], counts?: [number]}]
 * mutedIndexes: cột chưa chốt sổ, chấm Total vẽ nhạt; tipExtra(index) thêm dòng vào tooltip của chấm
 * target: ngưỡng trần (vd 0.02), vẽ nét đứt đỏ như mốc 80% của Pareto; null = không vẽ
 */
const StackedBarLine = ({cats, series, pct = true, height = 330, bottom = 42, xLabel, aria, mutedIndexes = [], tipExtra, target = null}) => {
    const [ref, width] = useContainerWidth()
    const tooltip = useTooltip()

    if (!cats.length || !series.length) {
        return <div ref={ref}><div className="empty">Không có dữ liệu trong khoảng đã chọn.</div></div>
    }

    const W = width || 340
    const H = height
    const L = 58, R = 14, T = 30, B = bottom
    const iw = W - L - R, ih = H - T - B, n = cats.length, slot = iw / n
    const bw = Math.max(6, Math.min(slot * 0.56, 58))
    const tops = cats.map((cat, p) => series.reduce((sum, s) => sum + (s.values[p] || 0), 0))
    // Kỳ không có mẫu số (mọi giá trị null): không vẽ chấm Total, đường đứt tại đó thay vì vẽ 0%
    const hasData = cats.map((cat, p) => series.some(s => s.values[p] !== null && s.values[p] !== undefined))
    const hasTarget = target > 0
    // Trục luôn chứa ngưỡng, kể cả khi mọi cột đều thấp hơn
    const max = (Math.max(...tops, hasTarget ? target : 0, 0) || 1) * 1.18
    const Y = (v) => T + ih - (v / max) * ih
    const muted = new Set(mutedIndexes)
    const markerR = n > 40 ? 2.6 : n > 20 ? 3.2 : 4
    const labelStep = Math.ceil(n / 26)
    const fmtAxis = (v) => pct ? `${(v * 100).toFixed(2)}%` : fmN(v)

    const bars = []
    cats.forEach((cat, p) => {
        const x = L + p * slot + slot / 2 - bw / 2
        let acc = 0
        series.forEach((s, j) => {
            const v = s.values[p]
            if (v === null || v === undefined || v <= 0) return

            const y0 = Y(acc + v), y1 = Y(acc)
            const hh = Math.max(0, y1 - y0 - (bw > 16 ? 1.2 : 0.4))
            const count = s.counts ? s.counts[p] : null
            const total = tops[p]
            bars.push(
                <rect
                    key={`${p}-${j}`} x={x} y={y0} width={bw} height={hh}
                    fill={`var(${s.color || SERIES_COLORS[j % SERIES_COLORS.length]})`}
                    style={{cursor: 'pointer'}}
                    {...tipHandlers(tooltip, (
                        <>
                            <div><b>{s.label}</b> · {cat}</div>
                            <div><span className="k">{pct ? 'Tỉ lệ lỗi' : 'Giá trị'}</span> <b>{pct ? fmP(v) : fmN(v)}</b></div>
                            {count !== null && count !== undefined && <div><span className="k">Số lỗi</span> <b>{fmN(count)}</b></div>}
                            <div><span className="k">Tỉ trọng trong cột</span> <b>{total ? `${((v / total) * 100).toFixed(1)}%` : '—'}</b></div>
                            <div><span className="k">Tổng cột</span> <b>{pct ? fmP(total) : fmN(total)}</b></div>
                        </>
                    ))}
                />
            )
            acc += v
        })
    })

    const points = cats.map((cat, p) => [L + p * slot + slot / 2, Y(tops[p])])
    const linePath = points.map((q, p) => hasData[p] ? `${p && hasData[p - 1] ? 'L' : 'M'}${q[0]} ${q[1]}` : '').join(' ')

    return (
        <div ref={ref}>
            <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={aria || 'biểu đồ cột chồng'}>
                {[0, 1, 2, 3, 4].map(i => {
                    const v = max * i / 4

                    return (
                        <g key={`grid-${i}`}>
                            <line x1={L} x2={L + iw} y1={Y(v)} y2={Y(v)} stroke="var(--grid)"/>
                            <text x={L - 6} y={Y(v) + 4} textAnchor="end" fontSize="10" fontFamily={MONO} fill="var(--ink-3)">{fmtAxis(v)}</text>
                        </g>
                    )
                })}
                {bars}
                {hasTarget && (
                    // Vẽ đè lên cột nên không nhận chuột, để tooltip của cột bên dưới vẫn hiện; nhãn ngưỡng nằm ở chú thích
                    <line x1={L} x2={L + iw} y1={Y(target)} y2={Y(target)} stroke="var(--s8)" strokeDasharray="4 3" strokeWidth="1.5" pointerEvents="none"/>
                )}
                <path d={linePath} fill="none" stroke="var(--tot)" strokeWidth="2" strokeLinejoin="round"/>
                {points.map((q, p) => hasData[p] && (
                    <circle
                        key={`pt-${p}`} cx={q[0]} cy={q[1]} r={markerR} fill="var(--panel)" stroke="var(--tot)" strokeWidth="2"
                        opacity={muted.has(p) ? 0.35 : 1} style={{cursor: 'pointer'}}
                        {...tipHandlers(tooltip, (
                            <>
                                <div><b>Total</b> · {cats[p]}</div>
                                <div><span className="k">{pct ? 'Tỉ lệ lỗi tổng' : 'Tổng'}</span> <b>{pct ? fmP(tops[p]) : fmN(tops[p])}</b></div>
                                {tipExtra && tipExtra(p)}
                            </>
                        ))}
                    />
                ))}
                {cats.map((cat, p) => (tops[p] > 0 && (n <= 16 || p % Math.ceil(n / 16) === 0)) ? (
                    <text key={`top-${p}`} x={L + p * slot + slot / 2} y={Y(tops[p]) - 11} textAnchor="middle" fontSize="10.5" fontFamily={MONO} fontWeight="700" fill="var(--tot)" opacity={muted.has(p) ? 0.45 : 1}>
                        {pct ? `${(tops[p] * 100).toFixed(2)}%` : fmN(tops[p])}
                    </text>
                ) : null)}
                {cats.map((cat, p) => p % labelStep === 0 ? (
                    <text key={`x-${p}`} x={L + p * slot + slot / 2} y={T + ih + 15} textAnchor="middle" fontSize="10" fill="var(--ink-2)">{cat}</text>
                ) : null)}
                {xLabel && <text x={L + iw / 2} y={H - 5} textAnchor="middle" fontSize="10.5" fill="var(--ink-3)">{xLabel}</text>}
                <line x1={L} x2={L + iw} y1={T + ih} y2={T + ih} stroke="var(--line-2)"/>
            </svg>
        </div>
    )
}

export default StackedBarLine
